import React from 'react'
import { useI18n } from '../../i18n'
import { GovernanceListingSection } from '../governance/GovernanceListingSection'
import ControlHealthCard from './ControlHealthCard'
import { verifyOperationalControl } from '../../services/controlAssuranceApi'

type AssurancePayload = {
  enabled?: boolean
  degraded?: Array<{
    id: string
    controlHealth: string
    assuranceLevel: string
    operationalPressure: number
    explanations?: { summary?: string }
    canonicalControl?: { code: string }
    informationSystem?: { name: string }
  }>
  recovering?: Array<{
    id: string
    controlHealth: string
    canonicalControl?: { code: string }
  }>
  states?: Array<{
    id: string
    controlHealth: string
    assuranceLevel: string
    operationalPressure: number
    explanations?: { summary?: string }
    canonicalControl?: { code: string }
    informationSystem?: { name: string }
  }>
}

type Props = {
  assurance?: AssurancePayload | null
  onRefresh?: () => void
}

export default function OperationalAssurancePanel({ assurance, onRefresh }: Props) {
  const { t } = useI18n()
  const [busy, setBusy] = React.useState(false)

  if (!assurance?.enabled) return null

  const handleVerify = async (id: string) => {
    setBusy(true)
    try {
      await verifyOperationalControl(id)
      onRefresh?.()
    } finally {
      setBusy(false)
    }
  }

  const degraded = assurance.degraded || []
  const recovering = assurance.recovering || []

  return (
    <GovernanceListingSection
      title={t('controlAssurance.operationalAssurance', 'Operational assurance')}
      description={t(
        'controlAssurance.workflowHint',
        'Telemetry-backed control health from drift, evidence freshness, and remediation — not real-time certification.',
      )}
    >
      {recovering.length > 0 ? (
        <p className="text-xs text-blue-700 m-0 mb-2">
          {t('controlAssurance.recoveringControl', 'Recovering control')}: {recovering.map((r) => r.canonicalControl?.code).join(', ')}
        </p>
      ) : null}
      <div className="flex flex-col gap-2">
        {degraded.length === 0 ? (
          <p className="text-xs text-slate-500 m-0">{t('controlAssurance.noDegraded', 'No operationally degraded controls in scope.')}</p>
        ) : (
          degraded.map((s) => (
            <ControlHealthCard key={s.id} state={s as never} busy={busy} onVerify={handleVerify} />
          ))
        )}
      </div>
    </GovernanceListingSection>
  )
}
